import { useEffect, useState, useCallback } from "react";
import api, { apiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { company_name: "", contact_person: "", email: "", phone: "", address: "", tax_id: "", notes: "" };

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers", { params: { search: q } });
      setCustomers(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ company_name: c.company_name, contact_person: c.contact_person, email: c.email, phone: c.phone, address: c.address, tax_id: c.tax_id, notes: c.notes || "" });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer created");
      }
      setDialogOpen(false);
      load(search);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.company_name}? This action is logged in the audit trail.`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Customer deleted");
      load(search);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const field = (key, label, type = "text") => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        data-testid={`customer-form-${key.replace(/_/g, "-")}-input`}
      />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Customers</h1>
          <p className="mt-1 text-sm text-muted-foreground">Client records for quotations and equipment inward.</p>
        </div>
        <Button onClick={openCreate} data-testid="create-customer-button">
          <Plus className="mr-2 h-4 w-4" /> New Customer
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Search company, contact, email, tax ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="customer-search-input"
        />
      </div>

      <div className="overflow-x-auto rounded-md border border-border" data-testid="customers-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Contact Person</TableHead>
              <TableHead className="hidden md:table-cell">Email</TableHead>
              <TableHead className="hidden lg:table-cell">Phone</TableHead>
              <TableHead className="hidden lg:table-cell">Tax ID</TableHead>
              <TableHead className="w-[100px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></TableCell></TableRow>
            )}
            {!loading && customers.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">No customers found.</TableCell></TableRow>
            )}
            {customers.map((c) => (
              <TableRow key={c.id} className="cursor-pointer" onClick={() => setDetail(c)} data-testid={`customer-row-${c.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 shrink-0 text-primary" />
                    <span className="font-medium">{c.company_name}</span>
                  </div>
                </TableCell>
                <TableCell>{c.contact_person}</TableCell>
                <TableCell className="hidden md:table-cell">{c.email}</TableCell>
                <TableCell className="hidden lg:table-cell">{c.phone || "—"}</TableCell>
                <TableCell className="hidden font-mono text-xs lg:table-cell">{c.tax_id || "—"}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`delete-customer-${c.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="customer-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit Customer" : "New Customer"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {field("company_name", "Company Name *")}
            {field("contact_person", "Contact Person *")}
            {field("email", "Email *", "email")}
            {field("phone", "Phone")}
            {field("tax_id", "Tax ID / GSTIN")}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Address</Label>
              <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="customer-form-address-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="customer-form-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="customer-form-cancel-button">Cancel</Button>
            <Button onClick={save} disabled={saving || !form.company_name || !form.contact_person || !form.email} data-testid="customer-form-save-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? "Save Changes" : "Create Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-md" data-testid="customer-detail-sheet">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-xl">{detail.company_name}</SheetTitle>
              </SheetHeader>
              <dl className="mt-6 space-y-4 text-sm">
                {[
                  ["Contact Person", detail.contact_person],
                  ["Email", detail.email],
                  ["Phone", detail.phone],
                  ["Tax ID", detail.tax_id],
                  ["Address", detail.address],
                  ["Notes", detail.notes],
                  ["Customer Since", formatDate(detail.created_at)],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{k}</dt>
                    <dd className="mt-0.5">{v || "—"}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-8 flex gap-2">
                <Button size="sm" onClick={() => { setDetail(null); openEdit(detail); }} data-testid="customer-detail-edit-button">
                  <Pencil className="mr-2 h-3.5 w-3.5" /> Edit
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
